import { useEffect, useRef, useState } from "react";
import { qubbase } from "@/lib/qubbase";

export interface OnlineUser {
  user_id: string;
  username: string;
  avatar_path: string | null;
  online_at: string;
}

/** Who has the board open right now (realtime presence), plus a "someone is typing" broadcast. */
export function usePresence(me: OnlineUser | null) {
  const [online, setOnline] = useState<OnlineUser[]>([]);
  const [typing, setTyping] = useState<Record<string, number>>({});
  const [channel, setChannel] = useState<ReturnType<
    typeof qubbase.channel
  > | null>(null);

  const key = me?.user_id;
  const username = me?.username;
  const avatar = me?.avatar_path;

  useEffect(() => {
    if (!key) return;
    const ch = qubbase.channel("board-presence", {
      config: { presence: { key }, broadcast: { self: false } },
    });
    ch.on("presence", { event: "sync" }, () => {
      // One entry per user even when they have several tabs open.
      const users = Object.values(ch.presenceState())
        .map((metas) => metas[0] as unknown as OnlineUser | undefined)
        .filter((u): u is OnlineUser => Boolean(u));
      setOnline(users.sort((a, b) => a.username.localeCompare(b.username)));
    })
      .on("broadcast", { event: "typing" }, ({ payload }) => {
        const { username } = payload as { username: string };
        setTyping((t) => ({ ...t, [username]: Date.now() }));
      })
      .subscribe((status) => {
        if (status === "SUBSCRIBED")
          void ch.track({
            user_id: key,
            username: username ?? "",
            avatar_path: avatar ?? null,
            online_at: new Date().toISOString(),
          });
      });
    setChannel(ch);
    return () => {
      setChannel(null);
      void qubbase.removeChannel(ch);
    };
  }, [key, username, avatar]);

  // Forget "typing" after 3 seconds of silence.
  useEffect(() => {
    const t = setInterval(() => {
      setTyping((current) => {
        const fresh = Object.fromEntries(
          Object.entries(current).filter(([, at]) => Date.now() - at < 3000),
        );
        return Object.keys(fresh).length === Object.keys(current).length
          ? current
          : fresh;
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);

  // At most one "typing" broadcast every 1.5 s, however fast the user types.
  const lastSent = useRef(0);
  const notifyTyping = () => {
    if (!channel || !username || Date.now() - lastSent.current < 1500) return;
    lastSent.current = Date.now();
    void channel.send({
        type: "broadcast",
        event: "typing",
        payload: { username },
      });
  };

  return { online, typing: Object.keys(typing), notifyTyping };
}
